<template>
  <div class="simple-table-wrapper">
    <BCard no-body>
      <template #header>
        <div class="simple-table-header">
        <h4 class="simple-table-header-title">{{cardHeader}}</h4>
        <BButton class="btn-heartbeat white large" v-if="showSkipButton" @click="handleSkipButtonClick()">{{ labelSkipButton }}</BButton>
        <BButton class="btn-heartbeat white large" v-if="showLongFormSurveyButton" @click="handleLongFormButtonClick()">
          <AppIcon v-if="getLongFormState" class="icon-long-survey" name="check-square-o" scale="0.8"></AppIcon>
          {{labelLongFormSurveyButton}}
        </BButton>
        </div>
      </template>
      <BCardBody>
        <div class="row filter-container">
          <div class="col-md-9">
            <BInputGroup>
              <template #prepend>
                <span class="input-group-text input-group-search">
                <AppIcon class="icon-search" name="search"></AppIcon>
                </span>
              </template>
              <BFormInput :model-value="currentTab.filter" @update:model-value="currentTab.filter = $event" :placeholder="placeholderSearchText" class="filter-input" />
            </BInputGroup>
          </div>
          <div class="col-md-3">
            <BFormSelect :model-value="filterSelectedDate" @update:model-value="filterSelectedDate = $event" :options="optionsFilterDate" class="mb-3"></BFormSelect>
          </div>
        </div>
        <app-table 
          striped hover show-empty
          :items="items"
          :fields="fields"
          :current-page="currentPage"
          :per-page="perPage"
          :filter="currentTab.filter"
          v-model:sort-by="sortBy"
          v-model:sort-desc="sortDesc"
          @filtered="onFiltered"
          class="simple-table"
          v-bind:class="{'simple-table-empty': items.length === 0}"
        >
          <template #cell(title)="row">
            <div v-on:click.stop="onTableItemSelected(row.item)">
              <div>
                <template v-if="row.item.status">
                  <AppIcon v-if="row.item.status === 'published'" class="icon-published" name="check-square-o" scale="0.8"></AppIcon>
                  <AppIcon v-else-if="row.item.status === 'draft'" class="icon-draft" name="file-o" scale="0.8"></AppIcon>
                  <AppIcon v-else-if="row.item.status === 'pending'" class="icon-pending" name="clock-o"></AppIcon>
                </template>
                <template v-if="row.item.name">{{row.item.name}}</template>
                <template v-else-if="row.item.title">{{row.item.title}}</template>
              </div>
              <div v-if="row.item.description" class="sub-title" v-bind:class="{'sub-title-margin': row.item.status}">{{row.item.description}}</div>
            </div>
          </template>

          <template #cell(author)="row">
            <div v-on:click.stop="onTableItemSelected(row.item)">
              <template v-if="row.item.authorName">
                {{row.item.authorName}}
              </template>
              <template v-else>
                {{getAuthorName(row.item.author)}}
              </template>
            </div>
          </template>
          <template #cell(updated)="row">
            <div v-on:click.stop="onTableItemSelected(row.item)">
              <template v-if="row.item.updatedAt">
                {{ formatDurationDate(row.item.updatedAt) }}
              </template>
              <template v-else>
                {{ formatDurationDate(row.item.updateDate) }}
              </template>
            </div>
          </template>
          <template #cell(referenceId)="row">
            <div v-on:click.stop="onTableItemSelected(row.item)">
              <template v-if="row.item.referenceId">
                {{row.item.referenceId}}
              </template>
              <template v-else-if="row.item.referenceItems && row.item.referenceItems[0]">
                {{row.item.referenceItems[0].id}}
              </template>
            </div>
          </template>
        </app-table>
      </BCardBody>
      <BCardFooter class="simple-row">
        <div class="col-sm-6">
          <ul class="flex-container legend-container" v-if="showLegend">
            <li><span class="legend-title">Legend</span></li>
            <li class="legend-status-item"><AppIcon class="icon-published" name="check-square-o"></AppIcon><span class="legend-status">Published</span></li>
            <li class="legend-status-item"><AppIcon class="icon-draft" name="file-o"></AppIcon><span class="legend-status">Draft</span></li>
            <li class="legend-status-item"><AppIcon class="icon-pending" name="clock-o"></AppIcon><span class="legend-status">Pending Review</span></li>
          </ul>
        </div>
        <div class="col-sm-6">
          <ul class="flex-container flex-end paging-container">
            <li class="flex-item-pagination"><BPagination class="col-sm-3 text-md-right" :total-rows="currentTotalRows" :per-page="perPage" :model-value="currentPage" @update:model-value="currentPage = $event" /></li>
            <li class="flex-item"><BFormSelect class="col-sm-3 text-md-right filter-paging-select" :options="pageOptions" :model-value="perPage" @update:model-value="perPage = $event" /></li>
            <li><span>Per Page</span></li>
          </ul>
        </div>
      </BCardFooter>
    </BCard>
  </div>
</template>

<script>
import { BButton, BCard, BCardBody, BCardFooter, BFormInput, BFormSelect, BInputGroup, BPagination } from 'bootstrap-vue-next';
import AppIcon from '@/components/AppIcon.vue';
import AppTable from '@/components/AppTable.vue';
import utilHelpers from '../../utils/helpers';
import { EVENT_SIMPLE_TABLE_SKIP_BUTTON_CLICK, EVENT_SIMPLE_TABLE_LONG_SURVEY_CLICK, EVENT_SIMPLE_TABLE_ITEM_CLICK } from '../../store/events';

export default {
  props: {
    currentTab: {
      type: Object,
      default: () => {}
    },
    cardHeader: {
      type: String,
      default: ''
    },
    header: {
      type: String,
      default: ''
    },
    placeholderSearchText: {
      type: String,
      default: ''
    },
    items: {
      type: Array,
      default: () => []
    },
    fields: {
      type: Object,
      default: () => {}
    },
    labelSkipButton: {
      type: String,
      default: ''
    },
    showSkipButton: {
      type: Boolean,
      default: false
    },
    showLegend: {
      type: Boolean,
      default: false
    },
    labelLongFormSurveyButton: {
      type: String,
      default: ''
    },
    showLongFormSurveyButton: {
      type: Boolean,
      default: false
    },
    longFormSurvey: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      currentPage: 1,
      perPage: 5,
      pageOptions: [{text: 5, value: 5}, {text: 10, value: 10}, {text: 15, value: 15}],
      sortBy: null,
      sortDesc: false,
      filter: null,
      modalDetails: { index: '', data: '' },
      filterSelectedDate: 'a',
      currentTotalRows: 0,
      isLongSurvey: this.longFormSurvey,
      optionsFilterDate: [
        { value: null, text: 'Please select an option' },
        { value: 'a', text: 'Recent' }
      ]
    };
  },
  computed: {
    getLongFormState () {
      return this.isLongSurvey;
    }
  },
  components: {
    AppIcon,
    AppTable,
    BButton,
    BCard,
    BCardBody,
    BCardFooter,
    BFormInput,
    BFormSelect,
    BInputGroup,
    BPagination
  },
  methods: {
    getAuthorName (author) {
      if (author && author.profile) {
        if (author.profile.firstname && author.profile.lastname) {
          return author.profile.firstname + ' ' + author.profile.lastname;
        } else if (author.profile.lastname) {
          return author.profile.lastname;
        } else if (author.profile.firstname) {
          return author.profile.firstname;
        }
      }
      return '';
    },
    onFiltered (filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.currentTotalRows = filteredItems.length;
      this.currentPage = 1;
    },
    totalRows () {
      if (this.currentTotalRows === 0) {
        this.currentTotalRows = this.items.length;
      }
      return this.currentTotalRows;
    },
    handleSkipButtonClick () {
      this.$emit(EVENT_SIMPLE_TABLE_SKIP_BUTTON_CLICK);
    },
    handleLongFormButtonClick () {
      this.isLongSurvey = !this.isLongSurvey;
      this.$emit(EVENT_SIMPLE_TABLE_LONG_SURVEY_CLICK, this.isLongSurvey);
    },
    onTableItemSelected (item) {
      this.$emit(EVENT_SIMPLE_TABLE_ITEM_CLICK, item);
    },
    formatDurationDate (date) {
      return utilHelpers.durationDate(date);
    }
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
  $common-background-color: #F6F7F7;
  $color-legend: #56666D;
  $color-published: #00C450;
  $color-draft: #00B5E6;
  $color-pending: #7260BC;
  $color-primary: #00C450;
  $color-secondary: #00B5E6;
  $color-item-hover: #1674CD;
  $color-item-focus: #376173;

  $border-color-table: 1px solid #E2E0E2;

  .simple-table-wrapper {
    .sub-title {
      font-size: 11px;
    }
    .sub-title-margin {
      margin-left: 30px;
    }

    .card-footer {
      background-color: #FAFAFC;

      .pagination {
        display: flex;
        padding-left: 0;
        list-style: none;
      }

      ul li {
        padding: 0;
      }
    }

    .card-body {
      padding-right: 0;
      padding-left: 0;
      padding-bottom: 0;
    }

    .filter-container {
      margin-left: 15px;
      margin-right: 15px;

      .filter-input {
        border: 1px solid #E0DFE0;	border-radius: 2px; color: #58666E;
        border-left: none;
      }

      .custom-select {
        border: 1px solid #E0DFE0;	border-radius: 5px; color: #58666E;
      }
    }

    .flex-container {
      padding: 0;
      margin: 0;
      list-style: none;
      display: flex;
      align-items: center;
    }

    .simple-row {
      display: flex;
      flex-wrap: wrap;
    }

    .flex-start {
      justify-content: space-between;
    }

    .flex-end {
      justify-content: flex-end;
    }

    .flex-item {
      padding: 5px;
      margin: 5px;
      line-height: 50px;
      color: white;
    }

    .item-counter {
      border-radius: 41%;
      background: #CFDADD;
      width: 30px;
      text-align: center;
      margin-right: 10px;
      font-size: 14px;
      font-weight: bold;
      color: white;
    }

    .item-counter-text {
      color: #A6AEB1;
    }

    .icon-search {
      color: #969696;
    }

    .icon-published {
      color: $color-published;
    }

    .icon-long-survey {
      color: white;
    }

    .icon-draft {
      color: $color-draft;
    }

    .icon-pending {
      color: $color-pending;
    }

    .icon-published,
    .icon-draft,
    .icon-pending {
      margin-right: 15px;
    }

    .legend-container {
      height: 50px;
    }

    .legend-title {
      color: #556679;
      font-weight: bold;
      font-size: 16px;
    }

    .legend-status-item {
      margin-left: 35px;
    }

    .input-group-search {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      width: 32.12px;	border: 1px solid #E0DFE0;
      border-radius: 2px;
      background-color: #EDF1F2;

      .icon-search {
        color: #979797;
      }
    }
  }

  .simple-table {
    background-color: white;
    margin-bottom: 0;

    &.table-striped tbody tr {
      background-color: white;
      color: #555555;
    }

    &.table-striped tbody tr:hover,
    .table-selected,
    .table-selected > td,
    .table-selected:hover,
    .table-selected > td:hover{
      background-color: $color-item-hover;
      color: white;
      cursor: pointer;
      font-weight: normal;
    }

    thead th {
      border-bottom: $border-color-table;
    }

    td {
      border-top: $border-color-table;
    }
  }
  .simple-table.table-striped tbody tr:active,
  .simple-table.table-striped tbody tr:focus {
    background-color: $color-item-focus;
  }
  .simple-table-empty {
    &.b-table>thead>tr>th.sorting {
      cursor: default;
      user-select: none;
      pointer-events: none;
    }
    &.table-striped tbody tr {
      background-color: white;
      user-select: none;
      pointer-events: none;
    }
    &.table-striped tbody tr:hover {
      background-color: white;
      color: black;
      cursor: default;
    }
  }
  .simple-table-header{
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-top: 20px;
    margin-bottom: 17px;
    .button-skip {
      background-color: $color-item-hover;
      color: white;
      &:hover {
        background-color: $color-item-focus;
        color: white;
      }
    }
  }

</style>
